import React, { useState, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import DataTable from '../common/DataTable';
import { Modal, FormField, MultiSelect, PrimaryButton, SecondaryButton } from '../common/FormComponents';
import { categoriesAPI, mainCategoriesAPI } from '../../services/api';
import { COLORS } from '../../utils/constants';

const C = COLORS;

const CategoryPage = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({});
  const [mainOptions, setMainOptions] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const res = await categoriesAPI.getAll({ page, limit: 10, ...filter });
      setData(res.data.data);
      setTotalPages(res.data.totalPages);
    } catch (err) { console.error(err); }
  }, [page, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load main-category options for the many-to-many picker
  useEffect(() => {
    mainCategoriesAPI.getAll({ limit: 1000 })
      .then(res => setMainOptions(res.data.data.map(m => ({ value: m.id, label: m.name }))))
      .catch(err => console.error(err));
  }, []);

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.name) { setErrors({ name: 'Required' }); return; }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        name_hi: form.name_hi,
        description_hi: form.description_hi,
        main_category_ids: form.main_category_ids || [],
      };
      if (modal?.id) {
        await categoriesAPI.update(modal.id, payload);
      } else {
        await categoriesAPI.create(payload);
      }
      setModal(null); setForm({}); setErrors({}); fetchData();
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || 'Failed to save' });
    } finally { setLoading(false); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete sub category "${row.name}"?`)) return;
    try { await categoriesAPI.delete(row.id); fetchData(); }
    catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const openEdit = (row) => {
    setForm({ name: row.name, description: row.description, name_hi: row.name_hi, description_hi: row.description_hi, main_category_ids: row.main_category_ids || [] });
    setModal({ id: row.id }); setErrors({});
  };

  const columns = [
    { key: 'name', label: 'Sub Category', render: v => <span style={{ fontWeight: 600, color: C.g800 }}>{v}</span> },
    { key: 'description', label: 'Description', render: v => <span style={{ color: C.g500 }}>{v || '—'}</span> },
    { key: 'main_category_names', label: 'Main Categories', render: v => v
      ? <span style={{ fontSize: 12, color: C.g600 }}>{v}</span>
      : <span style={{ fontSize: 12, color: C.g400 }}>—</span> },
    { key: 'news_count', label: 'Articles', render: v => (
      <span style={{ fontSize: 12, fontWeight: 600, color: C.primary, background: C.primaryGhost, padding: '3px 10px', borderRadius: 6 }}>{v || 0}</span>
    )},
    { key: 'created_at', label: 'Created', render: v => new Date(v).toLocaleDateString() },
  ];

  return (
    <AppLayout title="Sub Category Management">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.g900 }}>Sub Categories</h2>
        <PrimaryButton onClick={() => { setForm({ main_category_ids: [] }); setErrors({}); setModal({}); }} icon={<Plus size={18} />}>Add Sub Category</PrimaryButton>
      </div>

      <DataTable columns={columns} data={data} page={page} totalPages={totalPages}
        onPageChange={setPage} searchPlaceholder="Search sub categories..."
        onSearch={s => setFilter(p => ({ ...p, search: s }))}
        onEdit={openEdit} onDelete={handleDelete}
      />

      {modal && (
        <Modal title={modal.id ? 'Edit Sub Category' : 'Add Sub Category'} onClose={() => setModal(null)}>
          {errors.submit && <div style={{ padding: '10px 14px', background: C.dangerLight, borderRadius: 10, marginBottom: 16, fontSize: 13, color: C.danger }}>{errors.submit}</div>}
          <FormField label="Name" name="name" value={form.name} onChange={handleChange} error={errors.name} required />
          <FormField label="Name (हिन्दी)" name="name_hi" value={form.name_hi} onChange={handleChange}
            placeholder="Leave blank to show the English name" />
          <FormField label="Description" name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Optional description..." />
          <MultiSelect label="Main Categories" options={mainOptions}
            value={form.main_category_ids || []}
            onChange={ids => setForm(p => ({ ...p, main_category_ids: ids }))}
            emptyText="No main categories yet — create them on the Main Categories page." />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <SecondaryButton onClick={() => setModal(null)}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleSubmit} disabled={loading}>{loading ? 'Saving...' : modal.id ? 'Update' : 'Create'}</PrimaryButton>
          </div>
        </Modal>
      )}
    </AppLayout>
  );
};

export default CategoryPage;
